import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useUiStore } from '../store/ui';
import { ThemedPanel } from '../components/ui/ThemedSurface';
import { WalletHeader } from '../components/wallet';
import { EPHUB_COLORS as C, EPHUB_MARKETS, EPHUB_STATS, type EpHubAction, type EpHubMarket } from '@/constants';

function tr(t: TFunction, key: string, fallback: string) {
  return t(key, { defaultValue: fallback }) as string;
}

function ToneBadge({ label, tone }: { label: string; tone: keyof typeof C.tones }) {
  const color = C.tones[tone];

  return (
    <span
      className="flex h-8 min-w-11 items-center justify-center rounded-lg border px-2 text-xs font-black"
      style={{
        borderColor: color,
        color,
        background: C.innerBg,
        boxShadow: `0 0 10px ${color}33`,
      }}
    >
      {label}
    </span>
  );
}

function ActionCard({ action }: { action: EpHubAction }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const color = C.tones[action.tone];

  return (
    <button
      type="button"
      onClick={() => navigate(action.route)}
      className="flex aspect-square w-full min-w-0 flex-col items-center justify-center rounded-lg border p-1 text-center transition active:scale-[0.97]"
      style={{
        borderColor: `${color}66`,
        background: C.innerBg,
        boxShadow: `inset 0 -3px 7px 2px ${color}24`,
      }}
    >
      <img src={action.icon} alt="" className="size-12 object-contain" draggable={false} />
      <div className="flex w-full min-w-0 flex-col gap-0.5">
        <span className="block w-full min-w-0 truncate text-[11px] font-semibold leading-snug" style={{ color: C.text }}>
          {tr(t, action.labelKey, action.labelFallback)}
        </span>
        <span className="block w-full min-w-0 truncate text-[9px] leading-snug" style={{ color: C.muted }}>
          {tr(t, action.sublabelKey, action.sublabelFallback)}
        </span>
      </div>
    </button>
  );
}

function StatCard() {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="mb-4" theme={C}>
      <div className="grid h-full grid-cols-2 items-center py-3">
        {EPHUB_STATS.map((stat, index) => {
          const value = stat.value.includes('.') ? t(stat.value) : stat.value;
          const color = C.tones[stat.tone];

          return (
            <div key={`${stat.value}-${stat.labelKey}`} className="relative flex flex-col items-center text-center gap-1">
              <div className="flex flex-col items-center justify-center gap-0.5">
                <img src={stat.icon} alt="" className="size-12 object-contain" draggable={false} />
                <span className="text-base font-semibold leading-none" style={{ color }}>{value}</span>
              </div>
              <span className="text-[11px] leading-none" style={{ color: C.muted }}>
                {tr(t, stat.labelKey, stat.labelFallback)}
              </span>
              {index < EPHUB_STATS.length - 1 && <span className="absolute right-0 top-1/2 h-10 w-px -translate-y-1/2" style={{ background: C.statDivider }} />}
            </div>
          );
        })}
      </div>
    </ThemedPanel>
  );
}

function MarketSection({ market }: { market: EpHubMarket }) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-4" theme={C}>
      <div className="mb-3 flex items-center gap-3">
        <ToneBadge label={market.badge} tone={market.tone} />
        <span className="text-sm font-semibold" style={{ color: C.text }}>
          {tr(t, market.titleKey, market.titleFallback)}
        </span>
        <span className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: C.liveBg, color: C.tones.green }}>
          {t('ecosystem.live')}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {market.actions.map((action) => <ActionCard key={action.route} action={action} />)}
      </div>
    </ThemedPanel>
  );
}

function EpHero() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden px-5 pb-5 pt-[92px]">
      <div className="relative flex items-center gap-4">
        <img src="/brand/ep-hub/ep-hub-trading.png" alt="" className="size-16 object-contain" draggable={false} />
        <div className="min-w-0 flex flex-col gap-0.5">
          <div className="bg-clip-text text-[26px] font-semibold leading-snug text-transparent" style={{ backgroundImage: C.heroTitle, textShadow: C.heroTitleShadow }}>
            {tr(t, 'epHub.title', 'EP 交易市场')}
          </div>
          <div className="text-[11px] leading-snug" style={{ color: C.muted }}>
            {tr(t, 'epHub.subtitle', 'EP1 / EP2 挂单撮合、求购与记录查询')}
          </div>
        </div>
      </div>
    </section>
  );
}

export function EpHub() {
  const { t } = useTranslation();
  const openDrawer = useUiStore((s) => s.openDrawer);

  return (
    <div className="relative min-h-full overflow-hidden pb-8" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <WalletHeader t={t} onMenu={openDrawer} />
        <EpHero />

        <main className="relative z-10 px-[22px] pb-5">
          <StatCard />
          <div className="flex flex-col gap-4">
            {EPHUB_MARKETS.map((market) => <MarketSection key={market.badge} market={market} />)}
          </div>
        </main>
      </div>
    </div>
  );
}
